<script setup>
import { computed, watch, onMounted } from 'vue'

const props = defineProps({
  selectedSubjects: {
    type: Array,
    required: true
  },
  levels: {
    type: Array,
    required: true
  },
  selectedSubjectLevel: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:selectedSubjectLevel'])

// Flatten subjects with their levels into individual options
const subjectLevelOptions = computed(() => {
  const options = []

  props.selectedSubjects.forEach(subject => {
    if (subject.level_ids && subject.level_ids.length > 0) {
      subject.level_ids.forEach(levelId => {
        // Find the level object from levels array
        const level = props.levels.find(l => l.id === levelId)

        options.push({
          subject_id: subject.id,
          subject_name: subject.name,
          subject_color: subject.color || '#6B7280',
          level_id: levelId,
          level_name: level ? level.name : `Level ${levelId}`,
          level_color: level ? level.color : '#6B7280',
          display: `${subject.name} - ${level ? level.name : `Level ${levelId}`}`
        })
      })
    }
  })

  return options
})

const handleChange = (event) => {
  const value = event.target.value
  if (value) {
    const [subjectId, levelId] = value.split('-').map(Number)
    const option = subjectLevelOptions.value.find(
      opt => opt.subject_id === subjectId && opt.level_id === levelId
    )
    emit('update:selectedSubjectLevel', option)
  } else {
    emit('update:selectedSubjectLevel', null)
  }
}

// Auto-select first option when options become available
watch(subjectLevelOptions, (newOptions) => {
  if (newOptions.length > 0 && !props.selectedSubjectLevel) {
    emit('update:selectedSubjectLevel', newOptions[0])
  }
}, { immediate: true })

// Also auto-select on mount if options are already available
onMounted(() => {
  if (subjectLevelOptions.value.length > 0 && !props.selectedSubjectLevel) {
    emit('update:selectedSubjectLevel', subjectLevelOptions.value[0])
  }
})
</script>

<template>
  <div class="mb-4">
    <label class="block text-sm font-medium text-gray-700 mb-2">
      Select Course
    </label>
    <select
      :value="selectedSubjectLevel ? `${selectedSubjectLevel.subject_id}-${selectedSubjectLevel.level_id}` : ''"
      @change="handleChange"
      class="w-auto px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-purple-500 text-sm"
    >
      <option
        v-for="(option, index) in subjectLevelOptions"
        :key="`${option.subject_id}-${option.level_id}`"
        :value="`${option.subject_id}-${option.level_id}`"
      >
        {{ option.display }}
      </option>
    </select>
    <p v-if="subjectLevelOptions.length === 0" class="text-xs text-gray-500 mt-2">
      Please select subjects and levels in the pricing section above
    </p>
  </div>
</template>
